import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { gameAPI } from '../utils/api';
import { Container, Form, Button, Alert, Spinner, Row, Col, Tabs, Tab } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useTheme } from '../context/ThemeContext';
import styles from '../pages/Admin/admin.module.css';

const GameForm = ({ isEditMode = false }) => {
  const { theme } = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('basic');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    region: 'Global',
    image: '',
    apiProvider: '',
    apiGameId: '',
    isActive: true,
    tags: [],
    packs: []
  });

  // Fetch game data in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      const fetchGame = async () => {
        try {
          const data = await gameAPI.getGameById(id);
          if (data.game) {
            setFormData({
              name: data.game.name || '',
              description: data.game.description || '',
              category: data.game.category || '',
              region: data.game.region || 'Global',
              image: data.game.image || '',
              apiProvider: data.game.apiProvider || '',
              apiGameId: data.game.apiGameId || '',
              isActive: data.game.isActive !== false,
              tags: Array.isArray(data.game.tags) ? data.game.tags : [],
              packs: Array.isArray(data.game.packs) ? data.game.packs : []
            });
          }
        } catch (err) {
          setError('Failed to load game data');
          console.error('Error fetching game:', err);
        } finally {
          setLoading(false);
        }
      };

      fetchGame();
    } else {
      setLoading(false);
    }
  }, [id, isEditMode]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleArrayChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value.split(',').map(tag => tag.trim()).filter(tag => tag)
    }));
  };

  const handlePackChange = (index, field, value) => {
    const updatedPacks = [...formData.packs];
    updatedPacks[index] = { ...updatedPacks[index], [field]: value };

    setFormData(prev => ({
      ...prev,
      packs: updatedPacks
    }));
  };

  const addPack = () => {
    setFormData(prev => ({
      ...prev,
      packs: [
        ...prev.packs,
        {
          name: '',
          packId: '',
          amount: 0,
          retailPrice: 0,
          discountPrice: 0,
          isActive: true
        }
      ]
    }));
  };

  const removePack = (index) => {
    const updatedPacks = formData.packs.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      packs: updatedPacks
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      if (isEditMode && id) {
        await gameAPI.admin.updateGame(id, formData);
        toast.success('Game updated successfully!');
      } else {
        await gameAPI.admin.createGame(formData);
        toast.success('Game created successfully!');
      }
      navigate('/games');
    } catch (err) {
      setError(err.message || 'Failed to save game. Please try again.');
      console.error('Error saving game:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container className="text-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <div className={styles.modalBackdrop}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h1 className={`${styles.modalTitle} ${styles.gradientText}`}>
            {isEditMode ? 'Edit Game' : 'Add New Game'}
          </h1>
          <button
            type="button"
            className={styles.closeButton}
            onClick={() => navigate('/games')}
          >
            ×
          </button>
        </div>

        <div className={styles.modalBody}>
          {error && <Alert variant="danger" className={styles.alert}>{error}</Alert>}

          <Form onSubmit={handleSubmit}>
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className={`mb-4 ${styles.navTabs}`}
              id="game-form-tabs"
            >
              <Tab eventKey="basic" title="Basic Info">
                <div className="mt-4">
                  <Row>
                    <Col md={8}>
                      <Form.Group className={`mb-3 ${styles.formGroup}`}>
                        <Form.Label className={styles.formLabel}>Game Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className={styles.formControl}
                        />
                      </Form.Group>

                      <Form.Group className={`mb-3 ${styles.formGroup}`}>
                        <Form.Label className={styles.formLabel}>Description</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          name="description"
                          value={formData.description}
                          onChange={handleChange}
                          required
                          className={`${styles.formControl} ${styles.textarea}`}
                        />
                      </Form.Group>

                      <Row>
                        <Col md={6}>
                          <Form.Group className={`mb-3 ${styles.formGroup}`}>
                            <Form.Label className={styles.formLabel}>Category</Form.Label>
                            <Form.Control
                              type="text"
                              name="category"
                              value={formData.category}
                              onChange={handleChange}
                              required
                              className={styles.formControl}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className={`mb-3 ${styles.formGroup}`}>
                            <Form.Label className={styles.formLabel}>Region</Form.Label>
                            <Form.Select
                              name="region"
                              value={formData.region}
                              onChange={handleChange}
                              className={`${styles.formControl} ${styles.formSelect}`}
                            >
                              <option value="Global">Global</option>
                              <option value="North America">North America</option>
                              <option value="Europe">Europe</option>
                              <option value="Asia">Asia</option>
                              <option value="Other">Other</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      </Row>

                      <Form.Group className={`mb-3 ${styles.formGroup}`}>
                        <Form.Label className={styles.formLabel}>Tags (comma separated)</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.tags.join(', ')}
                          onChange={(e) => handleArrayChange('tags', e.target.value)}
                          placeholder="e.g., action, rpg, multiplayer"
                          className={styles.formControl}
                        />
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group className={`mb-3 ${styles.formGroup}`}>
                        <Form.Label className={styles.formLabel}>Game Image URL</Form.Label>
                        {formData.image && (
                          <div className="mb-2">
                            <img
                              src={formData.image}
                              alt="Game preview"
                              className={`img-fluid rounded ${styles.gameImage}`}
                              style={{ maxHeight: '200px' }}
                            />
                          </div>
                        )}
                        <Form.Control
                          type="url"
                          name="image"
                          value={formData.image}
                          onChange={handleChange}
                          className={styles.formControl}
                        />
                      </Form.Group>

                      <Form.Group className={`mb-3 ${styles.formGroup} form-check`}>
                        <Form.Check
                          type="checkbox"
                          id="isActive"
                          name="isActive"
                          checked={formData.isActive}
                          onChange={handleChange}
                          label="Active"
                          className={styles.formLabel}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              </Tab>

              <Tab eventKey="api" title="API Integration">
                <div className="mt-4">
                  <Form.Group className={`mb-3 ${styles.formGroup}`}>
                    <Form.Label className={styles.formLabel}>API Provider</Form.Label>
                    <Form.Select
                      name="apiProvider"
                      value={formData.apiProvider}
                      onChange={handleChange}
                      className={`${styles.formControl} ${styles.formSelect}`}
                    >
                      <option value="">None (Manual Management)</option>
                      <option value="smile.one">Smile One</option>
                      <option value="yokcash">Yokcash</option>
                      <option value="hopestore">Hopestore</option>
                    </Form.Select>
                  </Form.Group>

                  {formData.apiProvider && (
                    <Form.Group className={`mb-3 ${styles.formGroup}`}>
                      <Form.Label className={styles.formLabel}>API Game ID</Form.Label>
                      <Form.Control
                        type="text"
                        name="apiGameId"
                        value={formData.apiGameId}
                        onChange={handleChange}
                        placeholder="Enter the game ID from the provider"
                        className={styles.formControl}
                      />
                    </Form.Group>
                  )}

                  {formData.apiProvider && (
                    <div className={`${styles.alert} ${styles.alertSuccess}`}>
                      <p className="mb-1">
                        <strong>Note:</strong> Make sure to configure the API credentials in the backend.
                      </p>
                      <p className="mb-0">
                        The system will automatically sync game data from the provider when this game is saved.
                      </p>
                    </div>
                  )}
                </div>
              </Tab>

              <Tab eventKey="packs" title="Game Packs">
                <div className="mt-4">
                  <div className={`d-flex justify-content-between align-items-center mb-3 ${styles.sectionTitle}`}>
                    <h5>Game Packs</h5>
                    <Button variant="outline-primary" size="sm" onClick={addPack} className={styles.buttonOutline}>
                      Add Pack
                    </Button>
                  </div>

                  {formData.packs.length === 0 ? (
                    <div className={`text-center py-4 ${styles.emptyState}`}>
                      <div className={styles.emptyStateIcon}>📦</div>
                      <p>No packs added yet. Click 'Add Pack' to get started.</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className={`table ${styles.table}`}>
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Pack ID</th>
                            <th>Amount</th>
                            <th>Price</th>
                            <th>Discount</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {formData.packs.map((pack, index) => (
                            <tr key={index}>
                              <td>
                                <Form.Control
                                  type="text"
                                  value={pack.name}
                                  onChange={(e) => handlePackChange(index, 'name', e.target.value)}
                                  required
                                  className={styles.formControl}
                                />
                              </td>
                              <td>
                                <Form.Control
                                  type="text"
                                  value={pack.packId}
                                  onChange={(e) => handlePackChange(index, 'packId', e.target.value)}
                                  required
                                  className={styles.formControl}
                                />
                              </td>
                              <td>
                                <Form.Control
                                  type="number"
                                  value={pack.amount}
                                  onChange={(e) => handlePackChange(index, 'amount', Number(e.target.value))}
                                  min="0"
                                  step="1"
                                  required
                                  className={styles.formControl}
                                />
                              </td>
                              <td>
                                <Form.Control
                                  type="number"
                                  value={pack.retailPrice}
                                  onChange={(e) => handlePackChange(index, 'retailPrice', Number(e.target.value))}
                                  min="0"
                                  step="0.01"
                                  required
                                  className={styles.formControl}
                                />
                              </td>
                              <td>
                                <Form.Control
                                  type="number"
                                  value={pack.discountPrice || ''}
                                  onChange={(e) => handlePackChange(index, 'discountPrice', Number(e.target.value))}
                                  min="0"
                                  step="0.01"
                                  placeholder="Optional"
                                  className={styles.formControl}
                                />
                              </td>
                              <td className="align-middle">
                                <Form.Check
                                  type="switch"
                                  id={`active-${index}`}
                                  checked={pack.isActive !== false}
                                  onChange={(e) => handlePackChange(index, 'isActive', e.target.checked)}
                                  label={pack.isActive !== false ? 'Active' : 'Inactive'}
                                />
                              </td>
                              <td className="align-middle">
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => removePack(index)}
                                  className={styles.buttonDanger}
                                >
                                  Remove
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </Tab>
            </Tabs>

            <div className={`d-flex justify-content-between mt-4 ${styles.sectionDivider}`}>
              <Button variant="outline-secondary" onClick={() => navigate('/games')} className={styles.buttonSecondary}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={saving} className={styles.buttonPrimary}>
                {saving ? (
                  <>
                    <Spinner as="span" size="sm" animation="border" role="status" aria-hidden="true" className="me-2" />
                    Saving...
                  </>
                ) : isEditMode ? (
                  'Update Game'
                ) : (
                  'Create Game'
                )}
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default GameForm;
